import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import moment from "moment";
import Links from "../../Data/Links";
import "./MetaCampaignDashboard.css";

const formatNumber = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const num = Number(value);
  if (Number.isNaN(num)) return value;
  return num.toLocaleString();
};

const formatNumberOne = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const num = Number(value);
  if (Number.isNaN(num)) return value;
  return num.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
};

const formatCurrency = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const num = Number(value);
  if (Number.isNaN(num)) return value;
  return `$${num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatPercent = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const num = Number(value);
  if (Number.isNaN(num)) return value;
  const display = num > 1 ? num : num * 100;
  return `${display.toFixed(2)}%`;
};

const toNumber = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

const getMetricValue = (row, keys, defaultValue = undefined) => {
  if (!row) return defaultValue;
  for (const key of keys) {
    if (row[key] !== undefined && row[key] !== null && row[key] !== "") {
      return row[key];
    }
  }
  return defaultValue;
};

const getStoreName = (row) => {
  return (
    row?.store_name ||
    row?.storeName ||
    row?.store ||
    row?.account_name ||
    row?.name ||
    "-"
  );
};

const getCreativeName = (row) => {
  return (
    row?.creative_name ||
    row?.creative ||
    row?.ad_name ||
    row?.adName ||
    "-"
  );
};

const getCreativeUrl = (row) => {
  return row?.creative_url || row?.image_url || row?.thumbnail || row?.thumb || "";
};

export default function MetaCampaignDashboard() {
  const now = moment();
  const [stores, setStores] = useState([]);
  const [selectedStore, setSelectedStore] = useState("");
  const [rangeId, setRangeId] = useState("mtd");
  const [campaignRowsRaw, setCampaignRowsRaw] = useState([]);
  const [adRowsRaw, setAdRowsRaw] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [creativeMenuOpen, setCreativeMenuOpen] = useState(false);
  const [visibleCreatives, setVisibleCreatives] = useState(new Set());
  const creativeMenuRef = useRef(null);

  const dateRanges = useMemo(() => {
    const mtdStart = now.clone().startOf("month");
    const yesterday = now.clone().subtract(1, "day");
    const mtdEnd = yesterday.isBefore(mtdStart) ? mtdStart : yesterday;
    const last7Start = mtdEnd.clone().subtract(6, "days");
    const last30Start = mtdEnd.clone().subtract(29, "days");
    const monthLabel = now.format("MMMM");

    return [
      {
        id: "mtd",
        label: `${monthLabel} MTD (${mtdStart.format("MMM D")}-${mtdEnd.format("D")})`,
        start: mtdStart,
        end: mtdEnd
      },
      {
        id: "last7",
        label: `Last 7 Days (${last7Start.format("MMM D")}-${mtdEnd.format("D")})`,
        start: last7Start,
        end: mtdEnd
      },
      {
        id: "last30",
        label: `Last 30 Days (${last30Start.format("MMM D")}-${mtdEnd.format("D")})`,
        start: last30Start,
        end: mtdEnd
      }
    ];
  }, [now]);

  const activeRange = dateRanges.find((range) => range.id === rangeId) || dateRanges[0];

  useEffect(() => {
    axios
      .get(Links.getAllStores)
      .then((res) => {
        const list = res.data?.stores || [];
        setStores(list);
        if (!selectedStore && list.length) {
          setSelectedStore(list[0].name || "");
        }
      })
      .catch((err) => {
        console.log("Meta stores fetch error:", err);
        setStores([]);
      });
  }, []);

  const extractList = (response) => {
    const data = response?.data || {};
    const candidate =
      data?.data?.rows ||
      data?.data?.data ||
      data?.data?.items ||
      data?.rows ||
      data?.items ||
      data?.results ||
      data?.data ||
      data;
    return Array.isArray(candidate) ? candidate : [];
  };

  const fetchData = () => {
    if (!activeRange) return;
    if (!selectedStore) {
      setError("Please select an account.");
      return;
    }

    setLoading(true);
    setError("");

    const payload = {
      store_name: selectedStore,
      start_date: activeRange.start.format("YYYY-MM-DD"),
      end_date: activeRange.end.format("YYYY-MM-DD")
    };

    Promise.allSettled([
      axios.post(Links.metaAdsCampaignsDaily, payload),
      axios.post(Links.metaAdsAdsDaily, payload)
    ])
      .then(([campaignResult, adResult]) => {
        const campaignList =
          campaignResult.status === "fulfilled" ? extractList(campaignResult.value) : [];
        const adList = adResult.status === "fulfilled" ? extractList(adResult.value) : [];

        setCampaignRowsRaw(campaignList);
        setAdRowsRaw(adList);

        if (campaignResult.status === "rejected" && adResult.status === "rejected") {
          setError("Failed to load Meta campaign data.");
        } else if (campaignResult.status === "rejected") {
          setError("Campaign summary failed to load. Ad data may still show.");
        } else if (adResult.status === "rejected") {
          setError("Ad data failed to load. Campaign summary may still show.");
        }
      })
      .catch((err) => {
        console.log("Meta campaign fetch error:", err);
        setCampaignRowsRaw([]);
        setAdRowsRaw([]);
        setError("Failed to load Meta campaign data.");
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchData();
  }, [rangeId, selectedStore]);

  const campaignRows = useMemo(() => {
    return campaignRowsRaw.map((row, index) => {
      const cost = toNumber(getMetricValue(row, ["spend", "cost", "amount_spent"]));
      const impressions = toNumber(getMetricValue(row, ["impressions", "IMPRESSIONS"]));
      const reach = toNumber(getMetricValue(row, ["reach", "REACH"]));
      const frequency = toNumber(getMetricValue(row, ["frequency", "FREQUENCY"]));
      const linkClicks = toNumber(getMetricValue(row, ["link_clicks", "clicks", "CLICKS"]));
      const lpv = toNumber(getMetricValue(row, ["lpv", "LPV", "landing_page_views"]));
      const leads = toNumber(getMetricValue(row, ["leads", "LEADS"]));
      const cpl = leads ? cost / leads : toNumber(getMetricValue(row, ["cpl", "CPL"]));
      const convRate = linkClicks ? (leads / linkClicks) * 100 : toNumber(getMetricValue(row, ["conv_rate", "conversion_rate"]));

      return {
        id: row?.campaign_id || row?.campaignId || index,
        name: row?.campaign_name || row?.campaignName || row?.campaign || "-",
        cost,
        impressions,
        reach,
        frequency,
        linkClicks,
        lpv,
        leads,
        cpl,
        convRate
      };
    });
  }, [campaignRowsRaw]);

  const campaignTotals = useMemo(() => {
    return campaignRows.reduce(
      (acc, row) => {
        acc.cost += row.cost;
        acc.impressions += row.impressions;
        acc.reach += row.reach;
        acc.linkClicks += row.linkClicks;
        acc.lpv += row.lpv;
        acc.leads += row.leads;
        return acc;
      },
      { cost: 0, impressions: 0, reach: 0, linkClicks: 0, lpv: 0, leads: 0 }
    );
  }, [campaignRows]);

  const summaryMetrics = useMemo(() => {
    const frequency = campaignTotals.reach ? campaignTotals.impressions / campaignTotals.reach : 0;
    const cpl = campaignTotals.leads ? campaignTotals.cost / campaignTotals.leads : 0;
    const convRate = campaignTotals.linkClicks
      ? (campaignTotals.leads / campaignTotals.linkClicks) * 100
      : 0;

    return { frequency, cpl, convRate };
  }, [campaignTotals]);

  const creatives = useMemo(() => {
    const map = new Map();

    adRowsRaw.forEach((row) => {
      const creativeName = getCreativeName(row);
      if (!creativeName || creativeName === "-") return;

      const cost = toNumber(getMetricValue(row, ["spend", "cost", "amount_spent"]));
      const impressions = toNumber(getMetricValue(row, ["impressions", "IMPRESSIONS"]));
      const reach = toNumber(getMetricValue(row, ["reach", "REACH"]));
      const linkClicks = toNumber(getMetricValue(row, ["link_clicks", "clicks", "CLICKS"]));
      const lpv = toNumber(getMetricValue(row, ["lpv", "LPV", "landing_page_views"]));
      const leads = toNumber(getMetricValue(row, ["leads", "LEADS"]));
      const frequency = toNumber(getMetricValue(row, ["frequency", "FREQUENCY"]));

      if (!map.has(creativeName)) {
        map.set(creativeName, {
          creative: creativeName,
          creativeUrl: getCreativeUrl(row),
          cost: 0,
          impressions: 0,
          reach: 0,
          linkClicks: 0,
          lpv: 0,
          leads: 0,
          frequencyWeighted: 0
        });
      }

      const entry = map.get(creativeName);
      entry.cost += cost;
      entry.impressions += impressions;
      entry.reach += reach;
      entry.linkClicks += linkClicks;
      entry.lpv += lpv;
      entry.leads += leads;
      entry.frequencyWeighted += frequency * impressions;
      if (!entry.creativeUrl && getCreativeUrl(row)) {
        entry.creativeUrl = getCreativeUrl(row);
      }
    });

    return Array.from(map.values()).map((entry) => {
      const cpl = entry.leads ? entry.cost / entry.leads : 0;
      const convRate = entry.linkClicks ? (entry.leads / entry.linkClicks) * 100 : 0;
      const frequency = entry.impressions ? entry.frequencyWeighted / entry.impressions : 0;
      return {
        ...entry,
        cpl,
        convRate,
        frequency
      };
    });
  }, [adRowsRaw]);

  useEffect(() => {
    setVisibleCreatives(new Set(creatives.map((creative) => creative.creative)));
  }, [creatives]);

  useEffect(() => {
    if (!creativeMenuOpen) return undefined;
    const handleOutside = (event) => {
      if (creativeMenuRef.current && !creativeMenuRef.current.contains(event.target)) {
        setCreativeMenuOpen(false);
      }
    };
    document.addEventListener("click", handleOutside);
    return () => document.removeEventListener("click", handleOutside);
  }, [creativeMenuOpen]);

  const visibleCreativeRows = useMemo(() => {
    if (!creatives.length) return [];
    return creatives.filter((creative) => visibleCreatives.has(creative.creative));
  }, [creatives, visibleCreatives]);

  const creativeLabel = useMemo(() => {
    if (!creatives.length) return "All Creatives";
    const total = creatives.length;
    const visibleCount = visibleCreatives.size;
    if (visibleCount === total) return `All Creatives (${total})`;
    if (visibleCount === 0) return "None selected";
    return `${visibleCount} of ${total} Creatives`;
  }, [creatives.length, visibleCreatives]);

  const creativeTotals = visibleCreativeRows.reduce(
    (acc, row) => {
      acc.cost += row.cost;
      acc.impressions += row.impressions;
      acc.reach += row.reach;
      acc.linkClicks += row.linkClicks;
      acc.lpv += row.lpv;
      acc.leads += row.leads;
      return acc;
    },
    { cost: 0, impressions: 0, reach: 0, linkClicks: 0, lpv: 0, leads: 0 }
  );

  const creativeTotalsDerived = {
    cpl: creativeTotals.leads ? creativeTotals.cost / creativeTotals.leads : 0,
    convRate: creativeTotals.linkClicks
      ? (creativeTotals.leads / creativeTotals.linkClicks) * 100
      : 0
  };

  return (
    <div className="meta-campaign-dashboard">
      <header className="meta-header">
        <h1>
          <span>FFUN</span> - Meta Campaign Performance
        </h1>
        <div className="meta-header-meta">
          <div>Meta Ads - Campaign Level</div>
          <div>{activeRange?.label}</div>
        </div>
      </header>

      <div className="meta-container">
        <div className="meta-controls">
          <label htmlFor="meta-account">Account:</label>
          <select
            id="meta-account"
            value={selectedStore}
            onChange={(event) => setSelectedStore(event.target.value)}
          >
            {stores.length === 0 && <option value="">Loading...</option>}
            {stores.map((store) => (
              <option key={store.id || store.name} value={store.name}>
                {store.name}
              </option>
            ))}
          </select>

          <label htmlFor="meta-range" className="meta-range-label">Date Range:</label>
          <select
            id="meta-range"
            value={rangeId}
            onChange={(event) => setRangeId(event.target.value)}
          >
            {dateRanges.map((range) => (
              <option key={range.id} value={range.id}>
                {range.label}
              </option>
            ))}
          </select>
        </div>

        {error && <div className="meta-error">{error}</div>}
        {loading && <div className="meta-loading">Loading Meta campaign data...</div>}

        <div className="meta-summary">
          <div className="meta-card">
            <div className="meta-card-label">Total Spend</div>
            <div className="meta-card-value meta-card-value--orange">
              {formatCurrency(campaignTotals.cost)}
            </div>
          </div>
          <div className="meta-card">
            <div className="meta-card-label">Impressions</div>
            <div className="meta-card-value">{formatNumber(campaignTotals.impressions)}</div>
          </div>
          <div className="meta-card">
            <div className="meta-card-label">Reach</div>
            <div className="meta-card-value">{formatNumber(campaignTotals.reach)}</div>
          </div>
          <div className="meta-card">
            <div className="meta-card-label">Frequency</div>
            <div className="meta-card-value">{formatNumberOne(summaryMetrics.frequency)}</div>
          </div>
          <div className="meta-card">
            <div className="meta-card-label">Leads</div>
            <div className="meta-card-value">{formatNumberOne(campaignTotals.leads)}</div>
          </div>
          <div className="meta-card">
            <div className="meta-card-label">Overall CPL</div>
            <div className="meta-card-value">{formatCurrency(summaryMetrics.cpl)}</div>
          </div>
          <div className="meta-card">
            <div className="meta-card-label">Conv. Rate</div>
            <div className="meta-card-value">{formatPercent(summaryMetrics.convRate)}</div>
          </div>
        </div>

        <div className="meta-section-title">Campaign Performance</div>
        <div className="meta-table-wrap">
          <table>
            <thead>
              <tr>
                <th>Campaign Name</th>
                <th>Cost</th>
                <th>Impr.</th>
                <th>Reach</th>
                <th>Freq.</th>
                <th>Link Clicks</th>
                <th>LPV</th>
                <th>Leads</th>
                <th>CPL</th>
                <th>Conv. Rate</th>
              </tr>
            </thead>
            <tbody>
              {campaignRows.length === 0 && !loading ? (
                <tr>
                  <td colSpan={10} className="meta-empty">
                    No campaign data for this account.
                  </td>
                </tr>
              ) : (
                campaignRows.map((row, index) => (
                  <tr key={`${row.id}-${index}`}>
                    <td title={row.name}>{row.name}</td>
                    <td>{formatCurrency(row.cost)}</td>
                    <td>{formatNumber(row.impressions)}</td>
                    <td>{formatNumber(row.reach)}</td>
                    <td>{formatNumberOne(row.frequency)}</td>
                    <td>{formatNumber(row.linkClicks)}</td>
                    <td>{formatNumber(row.lpv)}</td>
                    <td>{formatNumberOne(row.leads)}</td>
                    <td>{formatCurrency(row.cpl)}</td>
                    <td>{formatPercent(row.convRate)}</td>
                  </tr>
                ))
              )}
              {campaignRows.length > 0 && (
                <tr className="meta-total">
                  <td>Total ({campaignRows.length} campaigns)</td>
                  <td>{formatCurrency(campaignTotals.cost)}</td>
                  <td>{formatNumber(campaignTotals.impressions)}</td>
                  <td>{formatNumber(campaignTotals.reach)}</td>
                  <td>-</td>
                  <td>{formatNumber(campaignTotals.linkClicks)}</td>
                  <td>{formatNumber(campaignTotals.lpv)}</td>
                  <td>{formatNumberOne(campaignTotals.leads)}</td>
                  <td>{formatCurrency(summaryMetrics.cpl)}</td>
                  <td>{formatPercent(summaryMetrics.convRate)}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="meta-section-header">
          <div className="meta-section-title">Ad Performance</div>
          <div className="meta-filter" ref={creativeMenuRef}>
            <button
              type="button"
              className="meta-filter-btn"
              onClick={() => setCreativeMenuOpen((prev) => !prev)}
            >
              {creativeLabel}
            </button>
            <div className={`meta-filter-panel ${creativeMenuOpen ? "open" : ""}`}>
              <div className="meta-filter-actions">
                <button
                  type="button"
                  onClick={() => setVisibleCreatives(new Set(creatives.map((creative) => creative.creative)))}
                >
                  Select All
                </button>
                <button type="button" onClick={() => setVisibleCreatives(new Set())}>
                  Deselect All
                </button>
              </div>
              {creatives.map((creative) => {
                const checked = visibleCreatives.has(creative.creative);
                return (
                  <label className="meta-filter-item" key={creative.creative}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => {
                        const next = new Set(visibleCreatives);
                        if (checked) {
                          next.delete(creative.creative);
                        } else {
                          next.add(creative.creative);
                        }
                        setVisibleCreatives(next);
                      }}
                    />
                    {creative.creative}
                  </label>
                );
              })}
            </div>
          </div>
        </div>

        <div className="meta-table-wrap">
          <table>
            <thead>
              <tr>
                <th className="meta-creative-col">Creative</th>
                <th>Creative Name</th>
                <th>Cost</th>
                <th>Impr.</th>
                <th>Reach</th>
                <th>Freq.</th>
                <th>LPV</th>
                <th>Leads</th>
                <th>CPL</th>
                <th>Conv. Rate</th>
              </tr>
            </thead>
            <tbody>
              {visibleCreativeRows.length === 0 && !loading ? (
                <tr>
                  <td colSpan={10} className="meta-empty">
                    {creatives.length === 0 ? "No ad data for this account." : "No creatives selected."}
                  </td>
                </tr>
              ) : (
                visibleCreativeRows.map((row) => (
                  <tr key={row.creative}>
                    <td className="meta-creative-cell">
                      {row.creativeUrl ? (
                        <img
                          className="meta-creative-thumb"
                          src={row.creativeUrl}
                          alt={row.creative}
                          onError={(event) => {
                            event.currentTarget.style.display = "none";
                          }}
                        />
                      ) : (
                        <span className="meta-creative-placeholder">
                          {row.creative ? row.creative.charAt(0) : "-"}
                        </span>
                      )}
                    </td>
                    <td title={row.creative}>{row.creative}</td>
                    <td>{formatCurrency(row.cost)}</td>
                    <td>{formatNumber(row.impressions)}</td>
                    <td>{formatNumber(row.reach)}</td>
                    <td>{formatNumberOne(row.frequency)}</td>
                    <td>{formatNumber(row.lpv)}</td>
                    <td>{formatNumberOne(row.leads)}</td>
                    <td>{formatCurrency(row.cpl)}</td>
                    <td>{formatPercent(row.convRate)}</td>
                  </tr>
                ))
              )}
              {visibleCreativeRows.length > 0 && (
                <tr className="meta-total">
                  <td colSpan={2}>Total ({visibleCreativeRows.length} creatives)</td>
                  <td>{formatCurrency(creativeTotals.cost)}</td>
                  <td>{formatNumber(creativeTotals.impressions)}</td>
                  <td>{formatNumber(creativeTotals.reach)}</td>
                  <td>-</td>
                  <td>{formatNumber(creativeTotals.lpv)}</td>
                  <td>{formatNumberOne(creativeTotals.leads)}</td>
                  <td>{formatCurrency(creativeTotalsDerived.cpl)}</td>
                  <td>{formatPercent(creativeTotalsDerived.convRate)}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="meta-footer">
          Source: Meta Marketing API | Date range: {activeRange?.start.format("YYYY-MM-DD")} to {activeRange?.end.format("YYYY-MM-DD")}
        </div>
      </div>
    </div>
  );
}
